import { Component, OnInit, inject, Input, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { OutcomesEditService } from '../services/outcomes-edit.service';
import { HubLovService } from '../../../../core/services/hub-lov.service';
import { FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription, take } from 'rxjs';
import { OutcomeMetricModel, ProjectOutcomeModel } from '../../../../../hub_schema/hubTypes';
import { CreateOutcomeStateService } from './services/create-outcome-state.service';
import { OutcomeState } from './types/outcome-state';

@Component({
    selector: 'app-create-outcome-dialog',
    templateUrl: './create-outcome-dialog.component.html',
    styleUrls: ['./create-outcome-dialog.component.scss'],
    standalone: false
})
export class CreateOutcomeDialogComponent implements OnInit {
    // dependencies
    private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private outcomesEditService: OutcomesEditService = inject(OutcomesEditService);
    private hubLovService: HubLovService = inject(HubLovService);
    private dialogRef: MatDialogRef<CreateOutcomeDialogComponent> = inject(MatDialogRef<CreateOutcomeDialogComponent>);
    private dialogData: any = inject(MAT_DIALOG_DATA);
    private createOutcomeStateService: CreateOutcomeStateService = inject(CreateOutcomeStateService);
    private outcomeMetrics: OutcomeMetricModel[] | undefined;

    // wizard state (todo: maybe consider a single state object in the service?)
    public outcomeState: OutcomeState;
    public currentStepNumber: number = 0;

    public ngOnInit(): void {
        this.hubLovService.getOutcomeMetrics().pipe(take(1)).subscribe((outcomeMetrics: OutcomeMetricModel[]) => {
            this.outcomeMetrics = outcomeMetrics;
        });

        this.outcomeState = this.createOutcomeStateService.initialize(this.dialogData);

        this.outcomeState.outcomeWizardForm = new FormGroup({
            projectId: new FormControl(this.outcomeState.projectId),
            statement: new FormControl('', Validators.required),
            isPolicyLeverageOutcome: new FormControl(false),
            outcome: new FormGroup({
                outcomeMetricId: new FormControl(null, Validators.required),
                outcomeMetric: new FormControl(null),
                outcomeUnitId: new FormControl(null, Validators.required),
                outcomeUnit: new FormControl(null),
                ncsInterventionId: new FormControl(null),
                ncsIntervention: new FormControl(null),
                ncsCountryId: new FormControl(null),
                ncsCountry: new FormControl(null),
                spatialExtent: new FormControl(null)
            }),
            customOutcome: new FormGroup({
                indicatorName: new FormControl('', Validators.required),
                unit: new FormControl('', Validators.required),
                isCalculatedAsRate: new FormControl(false)
            }),
            targets: new FormArray([
                new FormGroup({
                    targetDate: new FormControl(null, [Validators.required, this.validateTargetDate]),
                    targetValue: new FormControl(null, Validators.required)
                })
            ])
        });
    }

    public get isNcsTabShowing(): boolean {
        return this.outcomeState.usingNcsCalculator && this.outcomeState.is2030Goals;
    }

    private isNcsRecalculationPending: boolean = false;

    // This just prevents the ExpressionChangedAfterItHasBeenCheckedError console error
    public ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    public get targetForm(): FormGroup {
        return (this.outcomeState.outcomeWizardForm!.controls.targets as FormArray).controls[0] as FormGroup;
    }

    public get ncsExtentControl(): FormControl {
        return (this.outcomeState.outcomeWizardForm!.controls.outcome as FormGroup).controls.spatialExtent as FormControl;
    }

    public isOutcomeDetailsStepCompleted(): boolean {
        let isValid = true;

        if (this.outcomeState.is2030Goals) {
            const outcomeForm = this.outcomeState.outcomeWizardForm!.controls.outcome as FormGroup;

            isValid = outcomeForm.controls.outcomeMetricId.valid
                && outcomeForm.controls.outcomeUnitId.valid
                && outcomeForm.controls.ncsInterventionId.valid;

        }
        else {
            const customOutcomeForm = this.outcomeState.outcomeWizardForm!.controls.customOutcome as FormGroup;

            isValid = customOutcomeForm.controls.indicatorName.valid
                && customOutcomeForm.controls.unit.valid;
        }

        isValid = isValid && this.outcomeState.outcomeWizardForm!.controls.statement.valid;
        return isValid;
    }

    public isLocationAndSpatialExtentStepCompleted(): boolean {
        const isValid = (this.outcomeState.outcomeWizardForm!.controls.outcome as FormGroup).controls.ncsCountryId.valid
            && (this.outcomeState.outcomeWizardForm!.controls.outcome as FormGroup).controls.spatialExtent.valid;

        return isValid;
    }

    public isTargetStepCompleted(): boolean {
        return (this.outcomeState.outcomeWizardForm!.controls.targets as FormArray).valid;
    }

    private validateTargetDate(targetDateControl: FormControl): ValidationErrors | null {
        if (!targetDateControl.touched) {
            return null;
        }

        const targetDateObj = targetDateControl.value;
        const targetDate = new Date(targetDateObj.year, targetDateObj.month - 1, targetDateObj.day);

        const today = new Date();

        if (targetDate.getTime() < today.getTime()) {
            return {
                'invalid': 'Target Dates must be in the future.'
            };
        }
        return null;
    }

    public goals2020OutcomeTypeSelected(): void {
        this.outcomeState.is2030Goals = true;
        this.currentStepNumber = 1;
    }

    public customOutcomeTypeSelected(): void {
        this.outcomeState.is2030Goals = false;
        this.currentStepNumber = 1;
    }

    public isSaveDisabled(): boolean {
        return !this.isTargetStepCompleted() || (this.outcomeState.usingNcsCalculator && this.isNcsRecalculationPending);
    }

    public pendingNcsRecalculationChanged(isRecalcPending: boolean): void {
        this.isNcsRecalculationPending = isRecalcPending;
    }

    public createOutcome(): void {
        const projectOutcome = this.outcomeState.outcomeWizardForm!.value;

        if (this.outcomeState.is2030Goals) {
            projectOutcome.customOutcome = undefined;
            projectOutcome.outcome.outcomeMetric = this.outcomeMetrics?.find(om => om.outcomeMetricId === projectOutcome.outcome.outcomeMetricId);

            if (!this.outcomeState.usingNcsCalculator) {
                projectOutcome.outcome.ncsInterventionId = undefined;
                projectOutcome.outcome.ncsCountryId = undefined;
            }
        }
        else {
            projectOutcome.outcome = undefined;
        }

        projectOutcome.progress = [];
        this.createOutcomeStateService.dispose();
        this.dialogRef.close(projectOutcome);
    }

    public cancel(): void {
        this.dialogRef.close();
    }
}
